import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { useGoogleLogin } from "@react-oauth/google";
import { LazyGoogleOAuth } from "../components/LazyGoogleOAuth";
import Header from "../components/Header";
import { ArrowRight, Eye, EyeOff, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import SEOHead from "../components/SEOHead";
import { useToast } from "../hooks/useToast";

// Input sanitization helper
function sanitizeInput(value) {
  return value.replace(/[<>"'&]/g, (char) => {
    const map = { "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#x27;", "&": "&amp;" };
    return map[char] || char;
  });
}

function LoginContent() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(5);

  const requestedNext = searchParams.get("next");
  const emailErrorId = "login-email-error";
  const passwordErrorId = "login-password-error";
  const formErrorId = "login-form-error";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const cleanEmail = sanitizeInput(email.trim());
    const cleanPassword = sanitizeInput(password);

    const r = await login(cleanEmail, cleanPassword);
    setLoading(false);

    if (r.ok) {
      setSuccess(true);
      addToast({
        type: "success",
        title: t("login.welcome_back", "Welcome back!"),
        description: t("login.login_success", "Successfully signed in."),
      });
      // Redirect based on role: admin goes to /admin, users go to /dashboard
      // Unless a specific "next" page was requested
      const redirectTo = requestedNext || (r.data?.role === "admin" ? "/admin" : "/dashboard");
      setTimeout(() => navigate(redirectTo, { replace: true }), 1200);
    } else {
      setError(r.error);
      setAttemptsLeft((prev) => Math.max(0, prev - 1));
      addToast({
        type: "error",
        title: t("login.login_failed", "Sign in failed"),
        description: r.error || t("login.try_again", "Please check your credentials and try again."),
      });
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError("");
      setLoading(true);
      try {
        const { data } = await api.post("/auth/google/token", {
          access_token: tokenResponse.access_token,
        });
        setUser(data);
        setSuccess(true);
        addToast({
          type: "success",
          title: t("login.welcome_back", "Welcome back!"),
          description: t("login.google_success", "Signed in with Google."),
        });
        setTimeout(() => navigate(next, { replace: true }), 1200);
      } catch (e) {
        const errMsg = formatApiErrorDetail(e.response?.data?.detail) || t("login.google_error_fallback", "Google login failed. Try again.");
        setError(errMsg);
        setLoading(false);
        addToast({
          type: "error",
          title: t("login.google_failed", "Google sign in failed"),
          description: errMsg,
        });
      }
    },
    onError: () => {
      const errMsg = t("login.google_cancelled_fallback", "Google login was cancelled or failed.");
      setError(errMsg);
      addToast({
        type: "warning",
        title: t("login.google_cancelled", "Google sign in cancelled"),
        description: errMsg,
      });
    },
  });

  const onGoogle = () => googleLogin();

  if (success) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
        <SEOHead
          title={t('login.sign_in_max_employee')}
          description={t('login.sign_in_to_your_max_employee_account_to_manage_ai')}
          url="/login"
          noIndex
        />
        <div className="text-center animate-pulse" role="status" aria-live="polite">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" aria-hidden="true" />
          <h2 className="font-display text-2xl uppercase">{t('login.welcome_back', 'Welcome back!')}</h2>
          <p className="text-zinc-400 text-sm mt-2">{t('login.redirecting', 'Redirecting to dashboard...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />
      <main id="main-content" className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
        {/* Left visual */}
        <div className="hidden lg:block" aria-hidden="true">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-8">// SECURE TERMINAL</div>
          <h1 className="font-display text-5xl xl:text-6xl uppercase leading-[0.95]">Welcome<br/>back,<br/><span className="text-amber-500">{t('auth.operator_dot')}</span></h1>
          <p className="mt-8 text-zinc-400 max-w-md leading-relaxed">
            {t('login.subtitle', 'Authenticate to access your digital workers, downloads, and dashboard.')}
          </p>
          <div className="mt-12 border-l-2 border-amber-500 pl-6">
            <div className="font-mono text-xs text-zinc-500 leading-relaxed">
              {"> "} session.init()<br/>
              {"> "} verifying identity...<br/>
              {"> "} status: <span className="text-amber-500">awaiting_input</span>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="border border-zinc-900 bg-zinc-950 p-8 md:p-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3" aria-hidden="true">// LOGIN</div>
          <h2 className="font-display text-3xl uppercase mb-8" id="login-heading">{t('login.sign_in', 'Sign in')}</h2>

          <button
            data-testid="google-login-button"
            onClick={onGoogle}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white text-black hover:bg-amber-500 disabled:opacity-50 px-6 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t('login.continue_google', 'Continue with Google')}
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-zinc-900" aria-hidden="true" />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">{t('login.or_email', 'or with email')}</div>
            <div className="flex-1 h-px bg-zinc-900" aria-hidden="true" />
          </div>

          {error && (
            <div
              id={formErrorId}
              role="alert"
              aria-live="assertive"
              className="border border-red-900 bg-red-950/30 text-red-400 px-4 py-3 font-mono text-xs mb-5"
            >
              {error}
              {attemptsLeft < 5 && attemptsLeft > 0 && (
                <span className="block mt-1 text-amber-400">
                  {t("login.attempts_remaining", { count: attemptsLeft, defaultValue: "{{count}} attempts remaining" })}
                </span>
              )}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5" aria-describedby={error ? formErrorId : undefined} noValidate>
            <div>
              <label htmlFor="login-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">{t('login.email', 'EMAIL')}</label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                required
                aria-required="true"
                aria-invalid={error ? "true" : undefined}
                aria-describedby={error ? emailErrorId : undefined}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black border border-zinc-800 focus:border-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 px-4 py-3 text-white font-mono text-sm"
                placeholder={t("login.email_placeholder", "you@company.com")}
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="login-password" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">{t('login.password', 'PASSWORD')}</label>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  aria-required="true"
                  aria-invalid={error ? "true" : undefined}
                  aria-describedby={error ? passwordErrorId : undefined}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black border border-zinc-800 focus:border-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 px-4 py-3 pr-12 text-white font-mono text-sm"
                  placeholder={t("login.password_placeholder", "••••••••")}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-500 focus-visible:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm p-1 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors"
                  aria-label={showPassword ? t('login.hide_password', 'Hide password') : t('login.show_password', 'Show password')}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full group flex items-center justify-between bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black px-6 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
            >
              <span>{loading ? t('login.authenticating', 'Authenticating...') : t('login.sign_in_button', 'Sign in')}</span>
              {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-900 text-center">
            <span className="text-zinc-400 text-sm">{t('login.no_account', 'No account?')} </span>
            <Link to="/register" data-testid="link-register" className="text-amber-500 font-mono text-xs uppercase tracking-wider hover:text-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
              {t('login.create_one', 'Create one')} →
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Login() {
  return (
    <LazyGoogleOAuth>
      <LoginContent />
    </LazyGoogleOAuth>
  );
}
